import React, { useEffect, useState } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { C } from '../../utils/styles';
import Icon from '../Icon';
import api from '../../utils/api';

// ---------------------------------------------------------------------------
// E-Learning Coverage Row
// 3 donut charts at the bottom of the Internal Dashboard.
// Phase 18.1.1 — high-level overview only. Drill-down ships in 18.2 with the
// learner runtime + attempt tracking.
// ---------------------------------------------------------------------------

const AUDIENCE_META = {
  account_manager: { label: 'Account Managers', color: C.accent },
  employer:        { label: 'Employers',        color: C.purple },
  employee:        { label: 'Employees',        color: C.orange },
};

const STATUS_META = [
  { key: 'notStarted', label: 'Not Started', color: C.textLight },
  { key: 'inProgress', label: 'In Progress', color: C.accent },
  { key: 'completed',  label: 'Completed',   color: C.green },
  { key: 'failed',     label: 'Failed',      color: C.red },
];

function fmtPct(num, total) {
  if (!total || total <= 0) return '0%';
  return `${Math.round((num / total) * 1000) / 10}%`;
}

// Tiny donut tooltip
function ChartTooltip({ active, payload, total }) {
  if (!active || !payload || !payload.length) return null;
  const entry = payload[0];
  return (
    <div style={{
      background: '#fff',
      border: `1px solid ${C.border}`,
      borderRadius: 8,
      padding: '8px 12px',
      boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
      fontSize: 12,
      color: C.text,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600 }}>
        <span style={{ width: 10, height: 10, borderRadius: 2, background: entry.payload.fill || entry.color }} />
        {entry.name}
      </div>
      <div style={{ marginTop: 4, color: C.textMid }}>
        <span style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, color: C.text }}>{entry.value}</span>
        {total ? <span style={{ marginLeft: 6 }}>· {fmtPct(entry.value, total)}</span> : null}
      </div>
    </div>
  );
}

function PanelShell({ title, eyebrow, children, dataTestId }) {
  return (
    <div
      data-testid={dataTestId}
      style={{
        background: '#fff',
        border: `1px solid ${C.border}`,
        borderRadius: 12,
        padding: 20,
        boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 320,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
        <h3 style={{
          fontSize: 11, fontWeight: 700, color: C.textLight, letterSpacing: '0.1em',
          textTransform: 'uppercase', margin: 0, fontFamily: "'JetBrains Mono', monospace",
        }}>
          {eyebrow}
        </h3>
      </div>
      <div style={{ fontSize: 16, fontWeight: 700, color: C.text, marginBottom: 14 }}>
        {title}
      </div>
      {children}
    </div>
  );
}

function CenteredDonut({ data, total, centerValue, centerLabel, testId }) {
  const hasData = data.some((d) => (d.value || 0) > 0);
  return (
    <div style={{ position: 'relative', width: '100%', height: 180 }} data-testid={testId}>
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={hasData ? data : [{ name: 'Empty', value: 1, fill: C.borderLight, _empty: true }]}
            dataKey="value"
            nameKey="name"
            innerRadius={55}
            outerRadius={80}
            paddingAngle={hasData ? 2 : 0}
            startAngle={90}
            endAngle={-270}
            stroke="none"
          >
            {(hasData ? data : [{ name: 'Empty', value: 1, fill: C.borderLight }]).map((entry, idx) => (
              <Cell key={idx} fill={entry.fill} />
            ))}
          </Pie>
          {hasData && <Tooltip content={<ChartTooltip total={total} />} />}
        </PieChart>
      </ResponsiveContainer>
      {/* Center label */}
      <div style={{
        position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center', pointerEvents: 'none',
      }}>
        <div style={{
          fontSize: 28, fontWeight: 800, color: C.text,
          fontFamily: "'JetBrains Mono', monospace", letterSpacing: '-0.02em', lineHeight: 1,
        }}>
          {centerValue}
        </div>
        <div style={{
          fontSize: 10, color: C.textLight, fontWeight: 700,
          letterSpacing: '0.08em', textTransform: 'uppercase', marginTop: 4,
        }}>
          {centerLabel}
        </div>
      </div>
    </div>
  );
}

function Legend({ rows, total }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 14 }}>
      {rows.map((r) => (
        <div
          key={r.label}
          data-testid={`legend-${(r.testKey || r.label).toLowerCase().replace(/\s+/g, '-')}`}
          style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}
        >
          <span style={{ width: 10, height: 10, borderRadius: 2, background: r.color, flexShrink: 0 }} />
          <span style={{ color: C.textMid, flex: 1 }}>{r.label}</span>
          <span style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 600, color: C.text }}>
            {r.value}
          </span>
          {total > 0 ? (
            <span style={{ fontFamily: "'JetBrains Mono', monospace", color: C.textLight, width: 44, textAlign: 'right' }}>
              {fmtPct(r.value, total)}
            </span>
          ) : null}
        </div>
      ))}
    </div>
  );
}

function PendingBadge() {
  // Removed from UI per user request — kept here as a no-op so future revivals
  // (post Phase 18.2) can re-export without re-introducing the JSX from scratch.
  return null;
}

export default function LearningStatsRow({ isSuperUser }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    api.learning.dashboardStats()
      .then((data) => { if (mounted) { setStats(data); setError(null); } })
      .catch((err) => { if (mounted) setError(err.message || 'Failed to load training stats'); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, []);

  if (loading) {
    return (
      <SkeletonRow />
    );
  }

  if (error || !stats) {
    return (
      <div style={shellStyle} data-testid="learning-stats-error">
        <div style={{ color: C.textMid, fontSize: 13 }}>
          Couldn't load training stats: {error || 'unknown error'}
        </div>
      </div>
    );
  }

  const totalSignedUp = stats.signedUp?.total || 0;

  // ---- Panel 1: Signups by audience -------------------------------------------------
  const signupSegments = [
    { name: AUDIENCE_META.account_manager.label, value: stats.signedUp?.byRole?.account_manager || 0, fill: AUDIENCE_META.account_manager.color },
    { name: AUDIENCE_META.employer.label,        value: stats.signedUp?.byRole?.employer        || 0, fill: AUDIENCE_META.employer.color },
    { name: AUDIENCE_META.employee.label,        value: stats.signedUp?.byRole?.employee        || 0, fill: AUDIENCE_META.employee.color },
  ];

  // ---- Panel 2: Status breakdown ----------------------------------------------------
  const statusSegments = STATUS_META.map((s) => ({
    name: s.label,
    value: stats.status?.[s.key] || 0,
    fill: s.color,
    testKey: s.key,
  }));
  const completionRate = stats.completed?.ratePercent ?? 0;

  // ---- Panel 3: Completion progress per audience -----------------------------------
  // Whole = total signed up; segments = (completed AM) (completed Employer) (completed Employee) (still outstanding).
  const completedTotal = stats.completed?.total || 0;
  const outstanding = Math.max(0, totalSignedUp - completedTotal);
  const completionSegments = [
    { name: 'Completed · Account Managers', value: stats.completed?.byRole?.account_manager || 0, fill: AUDIENCE_META.account_manager.color, testKey: 'completed-am' },
    { name: 'Completed · Employers',        value: stats.completed?.byRole?.employer        || 0, fill: AUDIENCE_META.employer.color,        testKey: 'completed-employer' },
    { name: 'Completed · Employees',        value: stats.completed?.byRole?.employee        || 0, fill: AUDIENCE_META.employee.color,        testKey: 'completed-employee' },
    { name: 'Still Outstanding',            value: outstanding,                                    fill: C.borderLight,                       testKey: 'outstanding' },
  ];

  return (
    <div data-testid="learning-stats-row" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
        <div>
          <h2 style={{ fontSize: 14, fontWeight: 700, color: C.text, margin: 0, letterSpacing: '-0.01em' }}>
            <Icon name="school-outline" style={{ fontSize: 16, marginRight: 6, verticalAlign: '-2px', color: C.accent }} />
            E-Learning Coverage
          </h2>
          <div style={{ fontSize: 12, color: C.textLight, marginTop: 2 }}>
            {isSuperUser ? 'Across the full HealthCues portfolio' : 'Across your assigned book'}
            {' · '}
            {stats.courses?.publishedTotal || 0} published course{stats.courses?.publishedTotal === 1 ? '' : 's'}
            {stats.courses?.mandatoryTotal ? ` (${stats.courses.mandatoryTotal} mandatory)` : ''}
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }}>
        {/* Panel 1 — Signed Up by Role */}
        <PanelShell
          eyebrow="Signed Up"
          title={`Active learners: ${totalSignedUp}`}
          dataTestId="learning-panel-signups"
        >
          <CenteredDonut
            data={signupSegments}
            total={totalSignedUp}
            centerValue={totalSignedUp}
            centerLabel="Total"
            testId="signup-donut"
          />
          <Legend
            total={totalSignedUp}
            rows={signupSegments.map((s, i) => ({
              label: s.name,
              value: s.value,
              color: s.fill,
              testKey: ['ams', 'employers', 'employees'][i],
            }))}
          />
        </PanelShell>

        {/* Panel 2 — Status Breakdown */}
        <PanelShell
          eyebrow="Where Everyone Is"
          title="Training status"
          dataTestId="learning-panel-status"
        >
          <CenteredDonut
            data={statusSegments}
            total={totalSignedUp}
            centerValue={`${completionRate}%`}
            centerLabel="Completed"
            testId="status-donut"
          />
          <Legend
            total={totalSignedUp}
            rows={statusSegments.map((s) => ({
              label: s.name,
              value: s.value,
              color: s.fill,
              testKey: s.testKey,
            }))}
          />
        </PanelShell>

        {/* Panel 3 — Completion Progress */}
        <PanelShell
          eyebrow="Completion Progress"
          title={`${completedTotal} of ${totalSignedUp} done`}
          dataTestId="learning-panel-completion"
        >
          <CenteredDonut
            data={completionSegments}
            total={totalSignedUp}
            centerValue={`${completionRate}%`}
            centerLabel="Completion"
            testId="completion-donut"
          />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 14 }}>
            {(stats.byAudience || []).map((a) => {
              const meta = AUDIENCE_META[a.audience] || { label: a.audience, color: C.textMid };
              const pct = a.signedUp > 0 ? Math.round((a.completed / a.signedUp) * 1000) / 10 : 0;
              return (
                <div
                  key={a.audience}
                  data-testid={`completion-row-${a.audience}`}
                  style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}
                >
                  <span style={{ width: 10, height: 10, borderRadius: 2, background: meta.color, flexShrink: 0 }} />
                  <span style={{ color: C.textMid, flex: 1 }}>{meta.label}</span>
                  <span style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 600, color: C.text }}>
                    {a.completed}/{a.signedUp}
                  </span>
                  <span style={{ fontFamily: "'JetBrains Mono', monospace", color: C.textLight, width: 44, textAlign: 'right' }}>
                    {pct}%
                  </span>
                </div>
              );
            })}
          </div>
        </PanelShell>
      </div>
    </div>
  );
}

const shellStyle = {
  background: '#fff',
  border: `1px solid ${C.border}`,
  borderRadius: 12,
  padding: 20,
  boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
};

function SkeletonRow() {
  return (
    <div data-testid="learning-stats-row-skeleton" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }}>
      {[0, 1, 2].map((i) => (
        <div key={i} style={{ ...shellStyle, minHeight: 320, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ height: 12, width: '40%', background: C.borderLight, borderRadius: 4 }} />
          <div style={{ height: 18, width: '60%', background: C.borderLight, borderRadius: 4 }} />
          <div style={{ height: 160, background: C.borderLight, borderRadius: 8 }} />
          <div style={{ height: 10, width: '90%', background: C.borderLight, borderRadius: 4 }} />
          <div style={{ height: 10, width: '85%', background: C.borderLight, borderRadius: 4 }} />
        </div>
      ))}
    </div>
  );
}
